import React, { useState } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import ProjectCard from 'src/components/profile/ProjectCard';
import ProjectModal from 'src/components/profile/ProjectModal';
import Typography from 'src/components/ui/Typography';
import HorizontalScroller, { SnapItem } from 'src/components/ui/HorizontalScroller';
import Link from 'src/components/ui/Link';

import useModalOpenState from 'src/hooks/useModalOpenState';
import { cardHeightInRemForProjects } from 'src/constants/misc';

import type { Medium, Project, ProjectsQuery, UserProfileQuery } from 'src/types/__generated__/graphql';
import { useProjectsQuery } from 'src/hooks/__generated__/queries';
import theme from 'src/utils/theme';
import { routePathUser } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

const StyledLink = styledComponent(Link)`
    color: ${theme.palette.primary.main};
    font-size: 0.875rem;
    font-weight: bold;
`;

export interface Props {
    userProfile: NonNullable<UserProfileQuery['userProfile']>;
}

const defaultLimit = 5;

const ProjectsSection: React.FC<Props> = ({ userProfile }) => {
    const { t } = useTranslation(['profile', 'common']);
    const {
        previousData,
        data = previousData,
        loading,
        fetchMore,
    } = useProjectsQuery({
        variables: {
            skip: 0,
            take: defaultLimit,
            userId: userProfile.id,
        },
        skip: !userProfile.isProfessionnal,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const { props, open } = useModalOpenState();

    const [project, setProject] = useState<Project>();

    const projects = data?.projects ?? [];
    const cardWidthInRem = 12;
    return (
        <Card elevation={0}>
            <StyledCardContent>
                <Box mb="2rem">
                    <Typography variant="h2" serif>
                        {t('profile:myShowcase')}
                    </Typography>
                </Box>

                <HorizontalScroller
                    actualCardsLength={projects.length}
                    totalCardsLength={userProfile.numberOfProjects}
                    cardHeightInRem={cardHeightInRemForProjects}
                    cardWidthInPx={cardWidthInRem * 16} // 18rem * 16
                    loading={loading}
                    marginBottom={2}
                    handleFetchMore={() =>
                        fetchMore({
                            variables: {
                                skip: projects.length,
                            },
                            updateQuery(prev, { fetchMoreResult }: { fetchMoreResult?: ProjectsQuery }) {
                                if (!fetchMoreResult) return prev;
                                const prevItems = prev.projects ?? [];
                                const newItems = fetchMoreResult.projects ?? [];
                                return {
                                    projects: [...prevItems, ...newItems],
                                };
                            },
                        })
                    }
                >
                    {projects.map((_project) => (
                        <SnapItem key={_project.id}>
                            <Box width={`${cardWidthInRem}rem`}>
                                <ProjectCard
                                    onClick={() => {
                                        setProject(_project as Project);
                                        open();
                                    }}
                                    media={_project.media[0] as Medium}
                                    title={_project.name}
                                    subtitle={t('profile:pictureCount', {
                                        count: _project.media.length,
                                    })}
                                />
                            </Box>
                        </SnapItem>
                    ))}
                </HorizontalScroller>
                {userProfile.numberOfProjects > 0 && (
                    <Box textAlign="right">
                        <StyledLink href={`${routePathUser}/${userProfile.id}/projects`}>
                            {t('profile:seeAllShowcase')}
                        </StyledLink>
                    </Box>
                )}
                <ProjectModal {...props} project={project} />
            </StyledCardContent>
        </Card>
    );
};

export default ProjectsSection;
